import { create } from 'zustand';

export interface Message {
  id: string;
  conversation_id: string;
  sender_type: 'client' | 'agent' | 'system' | 'bot';
  sender_id?: string;
  content: string;
  message_type: 'text' | 'audio' | 'document' | 'image' | 'video';
  status: 'sent' | 'delivered' | 'read' | 'failed';
  is_internal_note: boolean;
  created_at: string;
  media_url?: string;
  media_filename?: string;
  media_mime_type?: string;
  media_size?: number;
}

export interface Conversation {
  id: string;
  client_id: string;
  client_name: string;
  client_phone: string;
  client_company?: string;
  client_priority: string;
  assigned_agent_id?: string;
  assigned_agent_name?: string;
  department?: string;
  assigned_department?: string;
  status: 'open' | 'waiting' | 'closed';
  priority: string;
  subject?: string;
  last_message?: string;
  last_message_at?: string;
  unread_count: number;
  started_at: string;
  /** Protocolo gerado na abertura do chamado */
  ticket_number?: string;
  /** Protocolo usado no fechamento (igual ao ticket_number) */
  ticket_protocol?: string;
  /** Avaliacao do cliente (1-5) apos finalizar */
  rating?: number | null;
  /** Feedback textual do cliente */
  rating_feedback?: string | null;
  /** Se true, nao envia solicitacao de avaliacao ao finalizar */
  skip_rating?: boolean;
  /** Departamento fixo configurado para o numero do cliente */
  fixed_department?: string | null;
  /** Número WhatsApp usado na conversa */
  wa_number_id?: string;
  wa_number_label?: string;
  wa_number_phone?: string;
  wa_number_phone_id?: string;
  notes?: string;
  /** Conversa de departamento nao permitido: dados sensiveis ja vem mascarados
   *  do servidor (issue #43). So usado no historico com include_restricted. */
  redacted?: boolean;
}

interface ChatState {
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Record<string, Message[]>;
  filter: 'all' | 'mine' | 'unassigned' | 'waiting';
  searchQuery: string;
  /** WebSocket caiu e está tentando reconectar (mostra aviso na UI do chat). */
  wsReconnecting: boolean;
  setWsReconnecting: (value: boolean) => void;
  setConversations: (conversations: Conversation[]) => void;
  setActiveConversation: (id: string | null) => void;
  updateConversation: (conversationId: string, updates: Partial<Conversation>) => void;
  addMessage: (conversationId: string, message: Message) => void;
  removeMessage: (conversationId: string, messageId: string) => void;
  setMessages: (conversationId: string, messages: Message[]) => void;
  addOrUpdateConversation: (conversation: Conversation) => void;
  setFilter: (filter: ChatState['filter']) => void;
  setSearch: (query: string) => void;
  clearUnread: (conversationId: string) => void;
}

export const useChatStore = create<ChatState>(set => ({
  conversations: [],
  activeConversationId: null,
  messages: {},
  filter: 'all',
  searchQuery: '',
  wsReconnecting: false,
  setWsReconnecting: value => set({ wsReconnecting: value }),
  setConversations: conversations => set({ conversations }),
  setActiveConversation: id => set({ activeConversationId: id }),
  updateConversation: (conversationId, updates) =>
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId ? { ...c, ...updates } : c,
      ),
    })),
  addMessage: (conversationId, message) =>
    set(state => {
      const list = state.messages[conversationId] || [];
      // Upsert por id: a mesma mensagem pode chegar pela resposta do POST de
      // envio E pelo evento new_message do WS (em qualquer ordem). A segunda
      // ocorrencia atualiza a primeira em vez de duplicar na conversa.
      const idx = list.findIndex(m => m.id === message.id);
      const next =
        idx >= 0 ? list.map((m, i) => (i === idx ? { ...m, ...message } : m)) : [...list, message];
      return {
        messages: { ...state.messages, [conversationId]: next },
      };
    }),
  removeMessage: (conversationId, messageId) =>
    set(state => ({
      messages: {
        ...state.messages,
        [conversationId]: (state.messages[conversationId] || []).filter(m => m.id !== messageId),
      },
    })),
  setMessages: (conversationId, messages) =>
    set(state => ({
      messages: { ...state.messages, [conversationId]: messages },
    })),
  addOrUpdateConversation: conversation =>
    set(state => {
      const exists = state.conversations.some(c => c.id === conversation.id);
      if (exists) {
        return {
          conversations: state.conversations.map(c =>
            c.id === conversation.id ? { ...c, ...conversation } : c,
          ),
        };
      }
      return { conversations: [conversation, ...state.conversations] };
    }),
  setFilter: filter => set({ filter }),
  setSearch: searchQuery => set({ searchQuery }),
  clearUnread: conversationId =>
    set(state => ({
      conversations: state.conversations.map(c =>
        c.id === conversationId ? { ...c, unread_count: 0 } : c,
      ),
    })),
}));
